import { cn } from "@heroui/react";
import type { LucideIcon } from "lucide-react";
import {
  BookOpenText,
  CircleCheck,
  MessageCircleMore,
  Search,
  Settings,
  Workflow,
  Wrench,
  createLucideIcon,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef } from "react";
import type { ExecutionTraceIcon, ExecutionTraceStep } from "../data/types";
import type { ExecutionTraceViewItem } from "../hooks/useInboxDemoPlayback";
import ShinyText from "./ShinyText";
import SmoothStreamText from "./SmoothStreamText";

const WAITING_COPY = "3chat Agent 等待执行中...";

const FileSearchCorner = createLucideIcon("file-search-corner", [
  [
    "path",
    {
      d: "M11.1 22H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.589 3.588A2.4 2.4 0 0 1 20 8v3.25",
      key: "file",
    },
  ],
  ["path", { d: "M14 2v5a1 1 0 0 0 1 1h5", key: "fold" }],
  ["path", { d: "m21 22-2.88-2.88", key: "handle" }],
  ["circle", { cx: "16", cy: "17", r: "3", key: "lens" }],
]);

const DatabaseSearch = createLucideIcon("database-search", [
  ["path", { d: "M21 11.693V5", key: "db-right" }],
  ["path", { d: "m22 22-1.875-1.875", key: "db-handle" }],
  ["path", { d: "M3 12a9 3 0 0 0 8.697 2.998", key: "db-mid" }],
  ["path", { d: "M3 5v14a9 3 0 0 0 9.28 2.999", key: "db-body" }],
  ["circle", { cx: "18", cy: "18", r: "3", key: "db-lens" }],
  ["ellipse", { cx: "12", cy: "5", rx: "9", ry: "3", key: "db-top" }],
]);

const TRACE_ICONS: Record<ExecutionTraceIcon, LucideIcon> = {
  bookOpenText: BookOpenText,
  fileSearchCorner: FileSearchCorner,
  circleCheck: CircleCheck,
  settings: Settings,
  databaseSearch: DatabaseSearch,
  workflow: Workflow,
  messageCircleMore: MessageCircleMore,
  wrench: Wrench,
  search: Search,
};

type ExecutionTraceWindowProps = {
  items: ExecutionTraceViewItem[];
  isRunning?: boolean;
  className?: string;
};

function TraceIcon({ name }: { name: ExecutionTraceIcon }) {
  const Icon = TRACE_ICONS[name];
  return (
    <Icon
      aria-hidden
      className="shrink-0 text-[#18181b]"
      height={14}
      strokeWidth={1.75}
      width={14}
    />
  );
}

function TraceStep({
  step,
  visibleLineCount,
}: {
  step: ExecutionTraceStep;
  visibleLineCount: number;
}) {
  const bullets = step.bullets ?? [];
  const visibleBullets = Math.max(0, Math.min(bullets.length, visibleLineCount - 1));
  const showNote = Boolean(step.note) && visibleLineCount > 1 + bullets.length;

  return (
    <div className="flex w-full flex-col gap-2">
      {visibleLineCount > 0 ? (
        <div className="flex flex-wrap items-center gap-1">
          <TraceIcon name={step.icon} />
          <SmoothStreamText
            className="text-xs leading-4 text-[#18181b]"
            text={step.title}
          />
          {step.code ? <SmoothStreamText asCode text={step.code} /> : null}
          {step.detail ? (
            <SmoothStreamText
              className="text-xs leading-4 text-[#71717a]"
              text={step.detail}
            />
          ) : null}
        </div>
      ) : null}
      {visibleBullets > 0 ? (
        <ul className="list-disc space-y-2 pl-5 text-xs leading-4 text-[#71717a]">
          {bullets.slice(0, visibleBullets).map((bullet) => (
            <li key={bullet}>
              <SmoothStreamText text={bullet} />
            </li>
          ))}
        </ul>
      ) : null}
      {showNote ? (
        <SmoothStreamText
          className="text-xs leading-4 text-[#71717a]"
          text={step.note ?? ""}
        />
      ) : null}
    </div>
  );
}

function ReplyTimeRule({ timestamp }: { timestamp: string }) {
  return (
    <div
      aria-label={`${timestamp} 回复分隔`}
      className="flex items-center gap-3 py-1"
      role="separator"
    >
      <span className="inbox-demo-trace-rule min-w-0 flex-1 border-t border-dashed border-default-300" />
      <time className="inbox-demo-trace-rule-label shrink-0 text-xs leading-4 text-default-400">
        {timestamp}
      </time>
      <span className="inbox-demo-trace-rule min-w-0 flex-1 border-t border-dashed border-default-300" />
    </div>
  );
}

function pinScrollToBottom(node: HTMLDivElement) {
  node.scrollTop = node.scrollHeight;
}

export default function ExecutionTraceWindow({
  items,
  isRunning = false,
  className,
}: ExecutionTraceWindowProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const node = bodyRef.current;
    if (!node) return;
    pinScrollToBottom(node);
  }, [items]);

  useEffect(() => {
    const node = bodyRef.current;
    const content = contentRef.current;
    if (!node || !content) return;

    const pin = () => pinScrollToBottom(node);
    const resizeObserver = new ResizeObserver(pin);
    const mutationObserver = new MutationObserver(pin);

    resizeObserver.observe(content);
    mutationObserver.observe(content, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    pin();

    let raf = 0;
    if (isRunning) {
      const loop = () => {
        pin();
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    const settle = window.setTimeout(pin, isRunning ? 0 : 900);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      cancelAnimationFrame(raf);
      window.clearTimeout(settle);
    };
  }, [isRunning, items]);

  return (
    <section
      aria-label="Agent 执行明细"
      className={cn(
        "inbox-demo-window light flex h-[300px] w-[340px] max-w-full flex-col overflow-hidden rounded-[16px] border border-[#e4e4e7] bg-white font-pingfang text-[#18181b]",
        className,
      )}
      data-theme="light"
      style={{ colorScheme: "light" }}
    >
      <header className="flex w-full shrink-0 items-center gap-3 rounded-t-[16px] border-b border-[#f4f4f5] bg-[#fafafa] px-4 py-2">
        <div aria-hidden className="flex h-[6px] w-[30px] shrink-0 items-center gap-[6px]">
          <span className="inbox-demo-traffic-dot inbox-demo-traffic-dot--close" />
          <span className="inbox-demo-traffic-dot inbox-demo-traffic-dot--min" />
          <span className="inbox-demo-traffic-dot inbox-demo-traffic-dot--max" />
        </div>
        <p className="min-w-0 flex-1 text-center text-sm font-semibold leading-5 text-[#18181b]">
          Agent 执行明细
        </p>
        <div aria-hidden className="h-[6px] w-[30px] shrink-0" />
      </header>

      <div
        className="inbox-demo-scroll min-h-0 flex-1 overflow-y-auto px-4 py-2"
        ref={bodyRef}
      >
        <div className="pb-4" ref={contentRef}>
          {items.length === 0 ? (
            <div className="py-2 text-xs leading-4">
              <ShinyText text={WAITING_COPY} />
            </div>
          ) : (
            <div className="flex flex-col gap-4 py-2">
              {items.map((item) =>
                item.type === "separator" ? (
                  <ReplyTimeRule key={item.id} timestamp={item.timestamp} />
                ) : (
                  <TraceStep
                    key={item.id}
                    step={item.step}
                    visibleLineCount={item.visibleLineCount}
                  />
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
